import { AbcCore, DotNetBridge, UrlHelper } from '@barthauer/abc/core';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { MapLoader } from './map.loader';
import { MapLayer } from './models/map.model';
import { MapService } from './services/map.service';

declare let ol: any;

export class MapView {
  private history: any = [];
  private history_current: number = -1;
  private initialRotationValue: number;

  constructor(
    public olmap: any,
    private mapService: MapService,
    public dataProjection: any,
    public featureProjection: any,
    private initialViewbox: number[],
    private getInitialRotationValue: () => Promise<number>,
    private res: ResourceSet,
  ) {
    this.updateHistory();
  }

  public getMap(): any {
    return this.olmap;
  }

  public zoomInitial(): void {
    if (this.initialViewbox?.length) {
      const mapSize = this.olmap.getSize();
      const viewPort = this.olmap.getViewport();
      const viewportSize = [viewPort.clientWidth, viewPort.clientHeight];

      if (viewportSize[0] === 0 && viewportSize[1] === 0) {
        this.olmap.getView().fit(this.initialViewbox);
      } else {
        this.olmap.getView().fit(this.initialViewbox, { size: this.olmap.getSize() });
      }
    } else if (this.history.length > 0) {
      this.history.push(this.history[0]);
      this.history_current++;
      this.updateCurrentViewRect();
    }
  }

  public zoomIn(): void {
    this.adjustZoom(+1);
  }

  public zoomOut(): void {
    this.adjustZoom(-1);
  }

  public canViewPrevious(): boolean {
    return this.history_current > 0;
  }

  public canViewNext(): boolean {
    return this.history_current < this.history.length - 1;
  }

  public tryViewPrevious(): boolean {
    if (this.canViewPrevious()) {
      this.history_current--;
      this.updateCurrentViewRect();

      return true;
    }
    return false;
  }

  public tryViewNext(): boolean {
    if (this.canViewNext()) {
      this.history_current++;
      this.updateCurrentViewRect();

      return true;
    }
    return false;
  }

  private updateCurrentViewRect(): boolean {
    if (this.history_current >= 0 && this.history_current < this.history.length) {
      const viewRect = this.history[this.history_current];
      this.olmap.getView().setCenter(viewRect.center);
      this.olmap.getView().setResolution(viewRect.resolution);
      return true;
    }
    return false;
  }

  private adjustZoom(delta: number): void {
    const view = this.olmap.getView();
    const zoom = view.getZoom();
    view.setZoom(zoom + delta);
  }

  public updateHistory(): void {
    if (this.olmap.getView().getAnimating()) return;

    while (this.history.length - 1 > this.history_current) {
      this.history.pop();
    }

    const lastViewRect = this.history.length > 0 ? this.history[this.history.length - 1] : null;
    const viewRect = {
      center: this.olmap.getView().getCenter(),
      resolution: this.olmap.getView().getResolution(),
    };
    if (
      !lastViewRect ||
      lastViewRect.center !== viewRect.center ||
      Math.abs(lastViewRect.resolution - viewRect.resolution) >= 0.00001
    ) {
      this.history.push(viewRect);
      this.history_current++;
    }
  }

  private dragZoom = undefined;
  public beginDragZoom(): void {
    this.dragZoom = new ol.interaction.DragZoom({
      condition: ol.events.condition.always,
    });

    this.olmap.addInteraction(this.dragZoom);
  }

  public endDragZoom(): void {
    if (this.dragZoom !== undefined) {
      this.olmap.removeInteraction(this.dragZoom);
      this.dragZoom = undefined;
    }
  }

  private dragBox = undefined;
  beginDragExtent() {
    this.dragBox = new ol.interaction.DragBox({
      condition: ol.events.condition.always,
    });

    this.olmap.addInteraction(this.dragBox);
    this.dragBox.on('boxend', () => this.onEndDragExtent());
  }
  onEndDragExtent() {
    if (this.dragBox !== undefined) {
      var extent = this.dragBox.getGeometry().getExtent();
      this.olmap.removeInteraction(this.dragBox);
      this.dragBox = undefined;

      this.mapService.raiseExtentSelected(extent);
    }
  }

  public fit(viewRect: any): void {
    viewRect = this.expandZeroAreaExtentIfApplied(viewRect);
    let timeout: number = 300;
    this.olmap.getView().fit(viewRect, {
      duration: timeout,
    });

    setTimeout(() => {
      this.olmap.getView().fit(viewRect);
    }, timeout * 2);
  }

  private ZOOM_EXTENT_BUFFER = 10.0;
  private ZOOM_OBJECT_RATIO = 0.1;

  public fitToObjects(viewRect: any, objectCount: number): void {
    let newRect = ol.extent.buffer(viewRect, this.ZOOM_EXTENT_BUFFER);
    if (objectCount != 1) {
      this.fit(newRect);
    } else {
      const result = this.shouldFitToObject(newRect);
      if (result.fit) {
        let additionalBuffer: number;
        let l = Math.max(result.width, result.height);
        let levels = 0;
        while (l >= 1.0) {
          l /= 100;
          levels++;
        }
        if (result.widthRatio > result.heightRatio) {
          additionalBuffer = result.width * Math.pow(this.ZOOM_OBJECT_RATIO, levels);
        } else {
          additionalBuffer = result.height * Math.pow(this.ZOOM_OBJECT_RATIO, levels);
        }
        if (additionalBuffer < 10) {
          additionalBuffer = 10;
        }
        newRect = ol.extent.buffer(newRect, additionalBuffer);
        this.fit(newRect);
      } else if (result.move) {
        this.setCenter(result.center);
      }
    }
  }

  private shouldFitToObject(objectRect: any): {
    move: boolean;
    fit: boolean;
    center: any;
    width: number;
    height: number;
    viewWidth: number;
    viewHeight: number;
    widthRatio: number;
    heightRatio: number;
  } {
    const center = ol.extent.getCenter(objectRect);
    const viewExtent = this.olmap.getView().calculateExtent();
    const viewWidth = ol.extent.getWidth(viewExtent);
    const viewHeight = ol.extent.getHeight(viewExtent);
    const width = ol.extent.getWidth(objectRect);
    const height = ol.extent.getHeight(objectRect);
    const widthRatio = width / viewWidth;
    const heightRatio = height / viewHeight;
    const move = !ol.extent.containsCoordinate(viewExtent, center);
    const fit = widthRatio < this.ZOOM_OBJECT_RATIO && heightRatio < this.ZOOM_OBJECT_RATIO;
    return {
      move: move,
      fit: fit,
      center: center,
      width: width,
      height: height,
      viewWidth: viewWidth,
      viewHeight: viewHeight,
      widthRatio: widthRatio,
      heightRatio: heightRatio,
    };
  }

  public setCenter(center: number[]): void {
    this.olmap.getView().animate(
      {
        center: center,
        duration: 1000,
      },
      () => {
        this.olmap.getView().setCenter(center);
      },
    );
  }

  public setCenterWithDataProjection(center: number[]): void {
    this.olmap.getView().setCenter(this.transformCoordinateFromDataProjection(center));
  }

  public setCenterWithWorldProjection(centerLonLat: number[]): void {
    this.olmap.getView().setCenter(this.transformCoordinateFromWorldProjection(centerLonLat));
  }

  private expandZeroAreaExtentIfApplied(ext: any): any {
    let ret = ext;
    if (ext !== undefined) {
      if (ol.extent.getWidth(ext) < this.ZOOM_EXTENT_BUFFER && ol.extent.getHeight(ext) < this.ZOOM_EXTENT_BUFFER)
        ret = ol.extent.buffer(ext, this.ZOOM_EXTENT_BUFFER);
    }

    return ret;
  }

  private refreshZoomDelta: number = 0.00001;
  public refresh(): void {
    this.adjustZoom(this.refreshZoomDelta);
    this.refreshZoomDelta = -this.refreshZoomDelta;
  }

  private refreshingDelayed: number;
  public refreshDelayed(): void {
    if (!this.refreshingDelayed) {
      this.refreshingDelayed = window.setTimeout(() => {
        this.refresh();
        this.refreshingDelayed = 0;
      }, 350);
    }
  }

  public setRotationEnabled(enabled: boolean): void {
    const vw = this.olmap.getView();
    const c = vw.getConstraints();
    if (enabled) {
      c.rotation = ol.rotationconstraint.none;
      vw.setRotation(this.initialRotationValue ?? 0.0);
      this.initialRotationValue = undefined;
      if (this.getInitialRotationValue) {
        this.getInitialRotationValue().then((r) => {
          this.initialRotationValue = r;
          if (r) {
            this.getInitialRotationValue = undefined;
            if (this.getRotationEnabled()) {
              this.setRotation(r);
            }
          }
        });
      }
    } else {
      this.initialRotationValue = this.getRotation();
      vw.setRotation(0.0);
      c.rotation = ol.rotationconstraint.disable;
    }
  }

  public getRotationEnabled(): boolean {
    const vw = this.olmap.getView();
    const c = vw.getConstraints();
    return c.rotation !== ol.rotationconstraint.disable;
  }

  public setRotation(value: number): void {
    this.olmap?.getView()?.setRotation(value);
  }

  public getRotation(): number {
    const vw = this.olmap?.getView();
    return vw?.getRotation();
  }

  public getCurrentScale(): number {
    const view = this.olmap.getView();

    const currentresolution: number = view.getResolution();
    const currentpointresolution: number = ol.proj.getPointResolution(
      view.getProjection(),
      currentresolution,
      view.getCenter(),
    );
    const newresolution: number = currentpointresolution / 1.000001; //hardcoded adjustment to ol-calculation...

    return MapLoader.getScaleFromResolution(newresolution);
  }

  public setCurrentScale(scale: number): void {
    MapView.setMapScale(this.olmap.getView(), scale);
  }

  // tslint:disable-next-line: member-ordering
  private static setMapScale(view: any, scale: number): void {
    const newresolution = this.calulateResolutionFromMapSale(view, scale);
    if (newresolution) {
      view.setResolution(newresolution);
    }
  }

  // tslint:disable-next-line: member-ordering
  private static calulateResolutionFromMapSale(view: any, scale: number): number {
    const newpointresolution: number = MapLoader.getResolutionFromScale(scale);
    const factor = this.getResolutionFactorForMapScaleCore(view, view.getCenter());
    const newresolution: number = newpointresolution * factor * 1.000001; //hardcoded adjustment to ol-calculation...
    return newresolution;
  }

  private static getResolutionFactorForMapScaleCore(view: any, center: number[]): number {
    if (view !== undefined) {
      const currentresolution: number = view.getResolution();
      const currentpointresolution: number = ol.proj.getPointResolution(
        view.getProjection(),
        currentresolution,
        center,
      );
      return currentresolution / currentpointresolution;
    }
    return undefined;
  }

  public getCurrentCenter(): number[] {
    return this.olmap.getView().getCenter();
  }

  private getCurrentExtent(): number[] {
    return this.olmap.getView().calculateExtent();
  }

  private setCurrentExtent(extent: number[]): void {
    this.olmap.getView().fit(extent);
  }

  private setCurrentCenter(center: number[]): void {
    this.olmap.getView().setCenter(center);
  }

  public ensureExtentVisible(extent: number[]): void {
    const currentExtent = this.getCurrentExtent();
    if (!ol.extent.containsExtent(currentExtent, extent)) {
      const size = ol.extent.getSize(extent);
      const expandValue = (0.25 * (size[0] + size[1])) / 2;
      const ext_e = ol.extent.buffer(extent, expandValue);
      this.setCurrentExtent(ext_e);
    }
  }

  public initializeRenderer(mapLoader: MapLoader) {
    if (DotNetBridge.hasRenderer) {
      DotNetBridge.renderer.onRender((name, session, settingsJson) => {
        if (name === 'openlayers') {
          const target: HTMLElement = this.olmap.getTargetElement();
          target.style.display = 'none';
          const settings = settingsJson ? JSON.parse(settingsJson) : null;
          const rotation: number = -1.0 * (settings?.rotation ?? 0);
          const oldRotation: number = this.getRotation();
          this.setRotationEnabled(rotation != 0);
          this.setRotation(rotation);

          const action = (_) => {
            this.setRotation(oldRotation);
            let result: string = null;
            if (_) {
              const arr = _.split(',');
              if (arr.length > 1 && arr[1]?.length > 0) {
                result = arr[1];
              }
            }
            DotNetBridge.renderer.setRenderResult(session, result);
          };
          const scale: number = settings?.mapScale ?? 1000;
          const extent: number[] = this.transformExtentFromDataProjection(settings?.extent);
          const dpi: number = settings?.dpi;
          this.printExtentAsDataUrl(mapLoader, scale, dpi, extent, action);
        }
      });
    }
  }

  private printExtentAsDataUrl(
    mapLoader: MapLoader,
    scale: number,
    dpi: number,
    extentInMeters: number[],
    action: (url) => void,
  ): void {
    const center: number[] = ol.extent.getCenter(extentInMeters);
    const sizeInMeters: number[] = ol.extent.getSize(extentInMeters);

    this.printAsDataUrl(mapLoader, scale, dpi, center, sizeInMeters, action, undefined, undefined);
  }

  public printAsDataUrl(
    mapLoader: MapLoader,
    scale: number,
    dpi: number,
    center: number[],
    sizeInMeters: number[],
    action: (url: string, errorLoadingLayers: MapLayer[]) => void,
    onAfterRestoreMap: () => void,
    onStateChange: (state: string) => void,
  ): void {
    try {
      const fullextent: number[] = this.getCurrentExtent();
      const size: number[] = this.olmap.getSize();
      const resolution: number = this.olmap.getView().getResolution();
      const dpiFactor: number = dpi ? dpi / MapLoader.getDpi() : 1.0;

      const origOpacity = this.adaptMapToPrintArea(mapLoader, scale, dpiFactor, center, sizeInMeters);
      mapLoader.renderMap(
        dpi,
        (errorLoadingLayers: MapLayer[]) =>
          this.onPrintComplete(
            mapLoader,
            size,
            fullextent,
            resolution,
            dpiFactor,
            origOpacity,
            action,
            onAfterRestoreMap,
            onStateChange,
            errorLoadingLayers,
          ),
        onStateChange,
      );
    } catch (e) {
      this.mapService.sendMapShortMessage(e.message);
      action(null, null);
    }
  }

  private onPrintComplete(
    mapLoader: MapLoader,
    size: number[],
    extent: number[],
    resolution: number,
    dpiFactor: number,
    origOpacity: unknown,
    action: (url: any, errorLoadingLayers: MapLayer[]) => void,
    onAfterRestoreMap: () => void,
    onStateChange: (state: string) => void,
    errorLoadingLayers: MapLayer[],
  ): any {
    try {
      onStateChange(this.res.get('Map-ExportingMap'));
      const image = this.getMapCanvasImage();
      action(image, errorLoadingLayers);
    } catch (e) {
      this.mapService.sendMapShortMessage(e.message);
      action(null, errorLoadingLayers);
    } finally {
      this.restorePreviousMap(mapLoader, size, extent, resolution, dpiFactor, origOpacity);
      if (onAfterRestoreMap) {
        onAfterRestoreMap();
      }
    }

    return {};
  }

  private getMapCanvasImage(): string {
    const targetCanvas = window.document.createElement('canvas');
    const size: number[] = this.olmap.getSize();

    targetCanvas.width = size[0];
    targetCanvas.height = size[1];
    const targetMapContext = targetCanvas.getContext('2d');

    this.olmap
      .getViewport()
      .querySelectorAll('.ol-layer canvas')
      .forEach((sourceCanvas) => {
        if (sourceCanvas?.width > 0) {
          const opacity = sourceCanvas.parentNode.style.opacity;
          targetMapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);
          const transform = sourceCanvas.style.transform;
          // Get the transform parameters from the style's transform matrix
          const matrix = transform
            .match(/^matrix\(([^\(]*)\)$/)[1]
            .split(',')
            .map(Number);

          CanvasRenderingContext2D.prototype.setTransform.apply(targetMapContext, matrix);
          targetMapContext.drawImage(sourceCanvas, 0, 0);
        }
      });
    if (!this.isCanvasContextEmpty(targetMapContext)) {
      return targetCanvas.toDataURL();
    }
  }

  private isCanvasContextEmpty(context: CanvasRenderingContext2D): boolean {
    try {
      var imageData = context.getImageData(0, 0, context.canvas.width, context.canvas.height);
      var data = imageData.data;
      for (var i = 0; i < data.length; i++) {
        if (data[i] !== 0) {
          return false;
        }
      }
      return true;
    } catch (e) {
      console.error(e);
      return true;
    }
  }

  private adaptMapToPrintArea(
    mapLoader: MapLoader,
    scale: number,
    dpiFactor: number,
    center: number[],
    sizeInMeters: number[],
  ): unknown {
    let opacity: unknown = '';
    if (center && sizeInMeters) {
      this.adaptLayerResolutions(mapLoader, dpiFactor);
      const style = this.olmap.getTargetElement()?.style;
      if (style) {
        opacity = style.opacity;
        style.opacity = 0.0;
      }

      const customMapSize = this.calculateMapSizeByExtent(sizeInMeters, scale / dpiFactor);
      this.olmap.setSize(customMapSize);

      this.setCurrentScale(scale / dpiFactor);
      this.setCurrentCenter(center);
    }
    return opacity;
  }

  private restorePreviousMap(
    mapLoader: MapLoader,
    mapSize: number[],
    extent: number[],
    resolution: number,
    dpiFactor: number,
    origOpacity: unknown,
    origDisplay: unknown = null,
  ) {
    this.adaptLayerResolutions(mapLoader, 1.0 / dpiFactor);

    this.olmap.setSize(mapSize);
    this.olmap.getView().setCenter(ol.extent.getCenter(extent));
    this.olmap.getView().setResolution(resolution);

    const style = this.olmap.getTargetElement()?.style;
    if (style) {
      style.opacity = origOpacity;
      if (origDisplay !== null) {
        style.display = origDisplay;
      }
    }
  }

  private adaptLayerResolutions(mapLoader: MapLoader, factor: number) {
    MapLoader.updateLayerResolutions(mapLoader.getAllLayers(), 1.0 / factor);
  }

  private calculateMapSizeByExtent(sizeInMeters: number[], scale: number) {
    const factor = MapLoader.getDpm() / scale;
    const width = sizeInMeters[0] * factor;
    const height = sizeInMeters[1] * factor;

    return [width, height];
  }

  public getCurrentExtentMapImage(mapLoader: MapLoader, size: number[], action: (url: string) => void): void {
    const style = this.olmap.getTargetElement()?.style;
    let opacity: unknown = '';
    let origDisplay: string;
    if (style) {
      origDisplay = style.display;
      style.display = 'none';
      opacity = style.opacity;
    }

    this.olmap.setSize(size);
    const extent: number[] = this.getCurrentExtent();
    const resolution: number = this.olmap.getView().getResolution();
    const dpiFactor: number = 1.0;

    try {
      mapLoader.renderMap(
        null,
        () => {
          this.olmap.setSize(size);
          action(this.getMapCanvasImage());
          this.restorePreviousMap(mapLoader, size, extent, resolution, dpiFactor, opacity, origDisplay);
        },
        undefined,
      );
    } catch (err) {
      action(null);
      this.restorePreviousMap(mapLoader, size, extent, resolution, dpiFactor, opacity, origDisplay);
    }
  }

  public transformGeometryToDataProjection(geom: any): any {
    if (geom && this.dataProjection && this.featureProjection) {
      geom = geom.clone().transform(this.featureProjection, this.dataProjection);
    }
    return geom;
  }

  public transformGeometryToFeatureProjection(geom: any): any {
    if (geom && this.dataProjection && this.featureProjection) {
      geom = geom.clone().transform(this.dataProjection, this.featureProjection);
    }
    return geom;
  }

  public transformExtentFromDataProjection(extent: number[]): number[] {
    if (extent && this.dataProjection && this.featureProjection) {
      extent = ol.proj.transformExtent(extent, this.dataProjection, this.featureProjection);
    }
    return extent;
  }

  private transformExtentToDataProjection(extent: number[]): number[] {
    if (extent && this.dataProjection && this.featureProjection) {
      extent = ol.proj.transformExtent(extent, this.featureProjection, this.dataProjection);
    }
    return extent;
  }

  private transformCoordinateFromDataProjection(coordinate: number[]): number[] {
    if (coordinate && this.dataProjection && this.featureProjection) {
      coordinate = ol.proj.transform(coordinate, this.dataProjection, this.featureProjection);
    }
    return coordinate;
  }

  private transformCoordinateFromWorldProjection(lonLat: number[]): number[] {
    return ol.proj.fromLonLat(lonLat, this.olmap.getView().getProjection());
  }

  private transformCoordinateToDataProjection(coordinate: number[]): number[] {
    if (coordinate && this.dataProjection && this.featureProjection) {
      coordinate = ol.proj.transform(coordinate, this.featureProjection, this.dataProjection);
    }
    return coordinate;
  }

  private transformCoordinateToWorldProjection(coordinate: number[]): number[] {
    return ol.proj.toLonLat(coordinate, this.olmap.getView().getProjection());
  }

  public getCurrentExtentWithDataProjection(): number[] {
    const extent = this.getCurrentExtent();
    return this.transformExtentToDataProjection(extent);
  }

  public getCurrentCenterWithDataProjection(): number[] {
    const center = this.getCurrentCenter();
    return this.transformCoordinateToDataProjection(center);
  }

  public getCurrentCenterWithWorldProjection(): number[] {
    const center = this.getCurrentCenter();
    return this.transformCoordinateToWorldProjection(center);
  }

  public async replaceTargetUrlValues(url: string, replaceProperty?: (name: string) => Promise<any>): Promise<string> {
    const regex = /{([^}]+)}/g;
    let match;
    while ((match = regex.exec(url))) {
      const name: string = match[1];
      let value = replaceProperty ? await replaceProperty(name) : '';
      if (!value && name === 'baseurl') {
        value = this.mapService.getWebApiBaseURL();
      } else if (!value && name === 'x') {
        value = this.getCurrentCenterWithDataProjection()[0];
      } else if (!value && name === 'y') {
        value = this.getCurrentCenterWithDataProjection()[1];
      } else if (!value && name === 'worldx') {
        value = this.getCurrentCenterWithWorldProjection()[0];
      } else if (!value && name === 'worldy') {
        value = this.getCurrentCenterWithWorldProjection()[1];
      } else if (!value && name === 'rotation') {
        value = (this.getRotation() * Math.PI) / 180.0;
      } else if (!value && name === 'scale') {
        value = this.getCurrentScale();
      }
      url = url.replace('{' + name + '}', value);
    }
    this.mapService.canOpenDataTable;
    return url;
  }

  public openUrl(abcCore: AbcCore, url: string, target: string, windowFeatures?: string): Window {
    const finalTarget = target || '_blank';
    if (!windowFeatures) {
      const parts = url.split('|');
      url = parts[0];
      if (parts.length > 2) {
        windowFeatures = parts[2];
      }
    }
    const finalFeatures = windowFeatures || 'location=yes,toolbar=yes,closebuttoncaption=Fertig';

    const wnd = abcCore.openWindow(
      UrlHelper.normalizeUrlSlashes(url.replace('{baseurl}', this.mapService.getWebApiBaseURL())),
      finalTarget,
      finalFeatures,
    );
    return wnd;
  }
}
